import { TFunction } from 'i18next';
import {
  LucideIcon,
  LayoutDashboard,
  MessagesSquare,
  Users,
  AlertTriangle,
  LayoutGrid,
  BookOpen,
  ClipboardList,
  ListChecks,
  GraduationCap,
  Wallet,
  FileText,
  Receipt,
  CalendarDays,
  UserRound,
  Video,
  BarChart3,
  UserPlus,
  FolderOpen,
  Plus,
  Briefcase,
  Landmark,
  TrendingUp,
} from 'lucide-react';

import { PATHS } from 'router/config';
import { PopupNames } from 'core';
import { UserRole } from 'types';
import {
  visibleFor,
  STUDENT_ROLES,
  EDUCATION_ROLES,
  FINANCE_VIEW_ROLES,
  FINANCE_EXPENSE_ROLES,
  FINANCE_PLAN_ROLES,
  ADMIN_ROLES,
  ZOOM_ROLES,
  SURVEY_ROLES,
  CAREER_ROLES,
  PERFORMANCE_ROLES,
  DOFIN_ROLES,
} from 'core/hooks';

import { ICommand, IPaletteRunContext } from './paletteTypes';

interface ICommandDef {
  id: string;
  allowed?: UserRole[];
  icon: LucideIcon;
  keywords: string[];
  run: (ctx: IPaletteRunContext) => void;
}

/**
 * The full command registry — navigation destinations + global «create» flows.
 * Titles resolve from `commandPalette.commands.<id>`; `keywords` are literal
 * ru/pl/en fuzzy-match aliases (not i18n leaves). Every def is role-gated so the
 * palette never offers a page the operator cannot reach.
 */
const COMMAND_DEFS: ICommandDef[] = [
  // --- Navigation --------------------------------------------------------
  {
    id: 'dashboard',
    allowed: STUDENT_ROLES,
    icon: LayoutDashboard,
    keywords: ['dashboard', 'дашборд', 'главная', 'панель', 'pulpit', 'kokpit'],
    run: (ctx) => ctx.history.push(PATHS.dashboard),
  },
  {
    id: 'communicator',
    allowed: STUDENT_ROLES,
    icon: MessagesSquare,
    keywords: [
      'communicator',
      'коммуникатор',
      'сообщения',
      'чат',
      'komunikator',
      'wiadomości',
    ],
    run: (ctx) => ctx.history.push(PATHS.communicator),
  },
  {
    id: 'students',
    allowed: STUDENT_ROLES,
    icon: Users,
    keywords: ['students', 'студенты', 'ученики', 'studenci', 'uczniowie'],
    run: (ctx) => ctx.history.push(PATHS.students),
  },
  {
    id: 'dofinansowanie',
    allowed: DOFIN_ROLES,
    icon: Landmark,
    keywords: [
      'dofinansowanie',
      'дофинансирование',
      'софинансирование',
      'bur',
      'kfs',
      'воронка',
      'lejek',
      'operatorzy',
      'операторы',
    ],
    run: (ctx) => ctx.history.push(PATHS.dofinansowanie),
  },
  {
    id: 'debtors',
    allowed: STUDENT_ROLES,
    icon: AlertTriangle,
    keywords: [
      'debtors',
      'должники',
      'долги',
      'задолженность',
      'dłużnicy',
      'zadłużeni',
    ],
    run: (ctx) => ctx.history.push(PATHS.debtors),
  },
  {
    id: 'groups',
    allowed: EDUCATION_ROLES,
    icon: LayoutGrid,
    keywords: ['groups', 'группы', 'grupy'],
    run: (ctx) => ctx.history.push(PATHS.settingsGroups),
  },
  {
    id: 'courses',
    allowed: EDUCATION_ROLES,
    icon: BookOpen,
    keywords: ['courses', 'курсы', 'kursy'],
    run: (ctx) => ctx.history.push(PATHS.settingsCourses),
  },
  {
    id: 'journals',
    allowed: EDUCATION_ROLES,
    icon: ClipboardList,
    keywords: [
      'journals',
      'журналы',
      'посещаемость',
      'dzienniki',
      'frekwencja',
    ],
    run: (ctx) => ctx.history.push(PATHS.settingsJournals),
  },
  {
    id: 'teachers',
    allowed: EDUCATION_ROLES,
    icon: GraduationCap,
    keywords: [
      'teachers',
      'преподаватели',
      'учителя',
      'nauczyciele',
      'wykładowcy',
    ],
    run: (ctx) => ctx.history.push(PATHS.teachers),
  },
  {
    id: 'finances',
    allowed: FINANCE_VIEW_ROLES,
    icon: Wallet,
    keywords: ['finances', 'финансы', 'деньги', 'finanse', 'pieniądze'],
    run: (ctx) => ctx.history.push(PATHS.financesDashboard),
  },
  {
    id: 'invoices',
    allowed: FINANCE_VIEW_ROLES,
    icon: FileText,
    keywords: ['invoices', 'счета', 'фактуры', 'faktury', 'rachunki'],
    run: (ctx) => ctx.history.push(PATHS.financesInvoices),
  },
  {
    id: 'expenses',
    allowed: FINANCE_EXPENSE_ROLES,
    icon: Receipt,
    keywords: ['expenses', 'расходы', 'траты', 'wydatki', 'koszty'],
    run: (ctx) => ctx.history.push(PATHS.financesExpenses),
  },
  {
    id: 'paymentCalendar',
    allowed: FINANCE_PLAN_ROLES,
    icon: CalendarDays,
    keywords: [
      'payment calendar',
      'платежный календарь',
      'календарь платежей',
      'kalendarz płatności',
    ],
    run: (ctx) => ctx.history.push(PATHS.financesPaymentCalendar),
  },
  {
    id: 'cashFlow',
    allowed: FINANCE_PLAN_ROLES,
    icon: TrendingUp,
    keywords: [
      'cash flow',
      'cashflow',
      'кэшфлоу',
      'денежный поток',
      'оддс',
      'przepływy pieniężne',
      'rpp',
    ],
    run: (ctx) => ctx.history.push(PATHS.financesCashFlow),
  },
  {
    id: 'users',
    allowed: ADMIN_ROLES,
    icon: UserRound,
    keywords: [
      'users',
      'пользователи',
      'персонал',
      'сотрудники',
      'użytkownicy',
      'personel',
    ],
    run: (ctx) => ctx.history.push(PATHS.users),
  },
  {
    id: 'zoom',
    allowed: ZOOM_ROLES,
    icon: Video,
    keywords: ['zoom', 'видео', 'записи', 'вебинары', 'nagrania', 'wideo'],
    run: (ctx) => ctx.history.push(PATHS.zoomDashboard),
  },
  {
    id: 'surveys',
    allowed: SURVEY_ROLES,
    icon: ListChecks,
    keywords: [
      'surveys',
      'опросы',
      'опрос',
      'анкеты',
      'анкета',
      'ankiety',
      'ankieta',
    ],
    run: (ctx) => ctx.history.push(PATHS.surveys),
  },
  {
    id: 'careerCenter',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: [
      'career',
      'career center',
      'карьера',
      'карьерный центр',
      'трудоустройство',
      'вакансии',
      'kariera',
      'centrum kariery',
      'zatrudnienie',
    ],
    run: (ctx) => ctx.history.push(PATHS.careerCenter),
  },
  // ТЗ 0040 доводка: шесть справочников были доступны ТОЛЬКО набором адреса.
  // В палитре они отдельными командами, а не одной: их ищут по имени
  // сущности — «медали», «профессии», «анкеты», — а не по слову «справочник».
  {
    id: 'careerSkills',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['навыки', 'справочник навыков', 'skills', 'umiejętności'],
    run: (ctx) => ctx.history.push(PATHS.careerSkills),
  },
  {
    id: 'careerProfessions',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['профессии', 'треки', 'professions', 'zawody', 'ścieżki'],
    run: (ctx) => ctx.history.push(PATHS.careerProfessions),
  },
  {
    id: 'careerMedals',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['медали', 'ачивки', 'очки', 'medals', 'odznaki', 'punkty'],
    run: (ctx) => ctx.history.push(PATHS.careerMedals),
  },
  {
    id: 'careerSiteCourses',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['курсы сайта', 'витрина', 'продажа', 'site courses', 'kursy'],
    run: (ctx) => ctx.history.push(PATHS.careerSiteCourses),
  },
  {
    id: 'careerLessons',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['уроки центра', 'обучение', 'career lessons', 'zajęcia centrum'],
    // Уроки и анкеты вернулись вкладками карьерного центра. Ведём палитру
    // сразу на вкладку, а не через редирект со старого адреса: лишний прыжок
    // видно глазом, и в истории браузера остаётся мусорный шаг.
    run: (ctx) => ctx.history.push(`${PATHS.careerCenter}?tab=lessons`),
  },
  {
    id: 'careerExercises',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['анкеты', 'анкеты уроков', 'exercises', 'ankiety lekcji'],
    run: (ctx) => ctx.history.push(`${PATHS.careerCenter}?tab=exercises`),
  },
  {
    id: 'performance',
    allowed: PERFORMANCE_ROLES,
    icon: BarChart3,
    keywords: [
      'performance',
      'успеваемость',
      'показатели',
      'эффективность',
      'wyniki',
      'efektywność',
    ],
    run: (ctx) => ctx.history.push(PATHS.performance),
  },
  // --- Global «create» ---------------------------------------------------
  {
    id: 'newStudent',
    allowed: STUDENT_ROLES,
    icon: UserPlus,
    keywords: [
      'new student',
      'add student',
      'создать студента',
      'добавить студента',
      'новый студент',
      'nowy uczeń',
      'dodaj ucznia',
    ],
    run: (ctx) => ctx.showPopup(PopupNames.addStudent),
  },
  {
    id: 'newGroup',
    allowed: EDUCATION_ROLES,
    icon: FolderOpen,
    keywords: [
      'new group',
      'add group',
      'создать группу',
      'добавить группу',
      'новая группа',
      'nowa grupa',
      'dodaj grupę',
    ],
    run: (ctx) => ctx.showPopup(PopupNames.addGroup),
  },
  {
    id: 'newTeacher',
    allowed: EDUCATION_ROLES,
    icon: GraduationCap,
    keywords: [
      'new teacher',
      'add teacher',
      'создать преподавателя',
      'добавить преподавателя',
      'nowy nauczyciel',
      'dodaj nauczyciela',
    ],
    run: (ctx) => ctx.showPopup(PopupNames.teacherAddPopup),
  },
  {
    id: 'newExpense',
    allowed: FINANCE_EXPENSE_ROLES,
    icon: Plus,
    keywords: [
      'new expense',
      'add expense',
      'создать расход',
      'добавить расход',
      'nowy wydatek',
      'dodaj wydatek',
    ],
    run: (ctx) => ctx.showPopup(PopupNames.expenseForm, {}),
  },
];

/** Build the role-visible command list; titles come from i18n. */
export const buildCommands = (roles: UserRole[], t: TFunction): ICommand[] =>
  COMMAND_DEFS.filter((def) => visibleFor(roles, def.allowed)).map((def) => ({
    id: def.id,
    title: t(`commandPalette.commands.${def.id}`),
    icon: def.icon,
    keywords: def.keywords,
    run: def.run,
  }));

/**
 * Fuzzy score of a lowercased `query` against a single `text`. Exact > prefix >
 * substring > subsequence; `0` = no match. Shared by the command ranker and the
 * page-action section so both rank the same way (webpack 4: no external lib).
 */
export const scoreText = (query: string, text: string): number => {
  const value = text.toLowerCase();
  if (value === query) return 100;
  if (value.startsWith(query)) return 80;
  const idx = value.indexOf(query);
  if (idx >= 0) return 55 - Math.min(idx, 20);
  // Subsequence fallback (fuzzy) — cheap, no external library (webpack 4).
  let qi = 0;
  for (let i = 0; i < value.length && qi < query.length; i += 1) {
    if (value[i] === query[qi]) qi += 1;
  }
  return qi === query.length ? 20 : 0;
};

/**
 * Rank a command against the raw query over its title + keywords. `0` means no
 * match (drop it); an empty query keeps everything at a flat baseline.
 */
export const scoreCommand = (rawQuery: string, command: ICommand): number => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return 1;
  return [command.title, ...command.keywords].reduce(
    (best, term) => Math.max(best, scoreText(query, term)),
    0,
  );
};
